import type { ApiResponse, SpreadsheetUndoRequest, SpreadsheetUndoResult } from '@bower/shared';
import type { HttpClient } from './http-client.types';

/** What the grid needs to join its unit. The token is held in memory only. */
export interface SpreadsheetSession {
  readonly unitId: string;
  readonly license: string;
  readonly canEdit: boolean;
  readonly sessionToken: string;
  readonly expiresInMs: number;
}

export function openSpreadsheetSession(
  http: HttpClient,
  artifactId: string,
): Promise<ApiResponse<SpreadsheetSession>> {
  return http.request({ method: 'POST', url: `/spreadsheets/${artifactId}/session` }, 0);
}

/**
 * Re-queue a failed import from the original upload. 202 on success; 409
 * `NOT_RETRYABLE` when the row is no longer a failed import.
 */
export function retrySpreadsheetImport(
  http: HttpClient,
  artifactId: string,
): Promise<ApiResponse<{ artifactId: string }>> {
  return http.request({ method: 'POST', url: `/spreadsheets/${artifactId}/retry-import` }, 0);
}

export type SpreadsheetExportFormat = 'xlsx' | 'csv';

export function exportSpreadsheet(
  http: HttpClient,
  artifactId: string,
  format: SpreadsheetExportFormat,
  sheetName?: string,
): Promise<ApiResponse<{ url: string; fileName: string }>> {
  return http.request(
    {
      method: 'POST',
      url: `/spreadsheets/${artifactId}/export`,
      data: { format, sheetName },
      timeout: 150_000,
    },
    0,
  );
}

export function createBlankSpreadsheet(
  http: HttpClient,
  projectId?: string,
): Promise<ApiResponse<{ artifactId: string }>> {
  return http.request(
    { method: 'POST', url: '/spreadsheets', data: { projectId } },
    // No retry: a create whose response was lost would make a second one.
    0,
  );
}

/**
 * Revert one agent batch with the token its change card carries. The token
 * goes in the body only, never the URL. No retry: a retried undo whose first
 * attempt landed would answer `SPREADSHEET_EDITED_SINCE` and hide the
 * `SPREADSHEET_UNDO_UNCONFIRMED` the first attempt earned.
 */
export function undoSpreadsheetChange(
  http: HttpClient,
  artifactId: string,
  body: SpreadsheetUndoRequest,
): Promise<ApiResponse<SpreadsheetUndoResult>> {
  return http.request(
    { method: 'POST', url: `/spreadsheets/${artifactId}/agent-undo`, data: body },
    0,
  );
}
